import { SidebarCollapsableMenu } from 'core/app-shell/sidebar/collapsible-menu/SidebarCollapsableMenu.tsx'
import { useOptionsPopover } from '../../core/popover/popover-hooks.tsx'
import { DefaultPopover } from '../../core/popover/DefaultPopover.tsx'
import { Box, CircularProgress } from '@mui/material'
import type { CollectionsTreeService } from '../services/CollectionsTreeService'
import { localStorageKeys } from '@/core/configs/local-storage-keys'
import { useRef } from 'react'
import { NewNodePopoverItems, type NewNodePopoverResult } from './NewNodePopoverItems.tsx'
import {
  HierarchyNodeOptionsPopoverItems,
  type NodeOptionPopoverItem,
} from './HierarchyNodeOptionsPopoverItemsProps.tsx'
import { CollectionTreeContext, type ICollectionTreeContext } from './ICollectionTreeContext.tsx'
import type { CollectionTreeController } from './CollectionsTreeController'
import { CollectionExplorerNodeMemo } from './CollectionNode.tsx'
import { useDiRef } from '@/common/react/di-hooks'
import { useBehaviorObservable } from '@/common/react/observable-hooks'
import { useNavigate } from 'react-router-dom'
import type { CollectionPageOpenData } from '@/objects/collection-view/CollectionPage.tsx'
import type { CollectionModel } from '@/core/gql/gql-models-override.ts'
import { useTutorialTarget } from '@/tutorial/TutorialContext'
import 'core/app-shell/sidebar/collapsible-menu/SidebarCollapsableMenu.css'

interface CollectionsExplorerProps {
  spaceId: number
}

export function CollectionsExplorer({ spaceId: _ }: CollectionsExplorerProps) {
  const navigate = useNavigate()
  const collectionsService = useDiRef<CollectionsTreeService>('CollectionsTreeService')
  const controller = useDiRef<CollectionTreeController>('CollectionTreeController')
  const tutorialTargetRef = useTutorialTarget('collections-explorer')
  const newNodePopoverHooks = useOptionsPopover<NewNodePopoverResult>()
  const nodeOptionsPopoverHooks = useOptionsPopover<NodeOptionPopoverItem>()
  void useBehaviorObservable(collectionsService.collectionsTreeVersion$)
  const contextRef = useRef({
    onCollectionClicked,
    newNodePopoverHooks,
    optionPopoverHooks: nodeOptionsPopoverHooks,
  } as ICollectionTreeContext)

  const tree = collectionsService.collectionsTree
  if (!tree) {
    return
  }
  const rootNodes = tree?.nodeToChildren.get(null) ?? []

  contextRef.current.tree = tree

  function onNewNodePopoverItemSelected(result: NewNodePopoverResult) {
    controller.onNewNodePopoverItemClicked(result, null)
  }

  function onCollectionClicked(collection: CollectionModel) {
    // controller.prefetchCollectionData(collection.id)
    void navigate(`/collection?id=${collection.id}`, {
      state: { fromApp: true } as CollectionPageOpenData,
    })
  }

  return (
    <>
      <CollectionTreeContext.Provider value={contextRef.current}>
        <div ref={tutorialTargetRef}>
          <SidebarCollapsableMenu
            title={'Objects'}
            newButtonText={'+ New'}
            onNewButtonClicked={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation()
              newNodePopoverHooks.openPopover(e.currentTarget, onNewNodePopoverItemSelected)
            }}
            isOpenLocalStorageKey={localStorageKeys.CollectionsExplorerOpen}
          >
            <Box sx={{ overflow: 'hidden' }}>
              {tree ? (
                rootNodes.map((id) => (
                  <CollectionExplorerNodeMemo
                    subtreeVersion={tree.idToSubtreeVersion.get(id)!}
                    key={id}
                    nodeId={id}
                    level={0}
                  />
                ))
              ) : (
                <CircularProgress />
              )}
            </Box>
          </SidebarCollapsableMenu>
        </div>
      </CollectionTreeContext.Provider>
      <DefaultPopover
        {...{ anchorEl: newNodePopoverHooks.anchorEl!, close: newNodePopoverHooks.closePopover }}
      >
        <NewNodePopoverItems onItemSelected={newNodePopoverHooks.setResult} />
      </DefaultPopover>
      <DefaultPopover
        {...{
          anchorEl: nodeOptionsPopoverHooks.anchorEl!,
          close: nodeOptionsPopoverHooks.closePopover,
        }}
      >
        <HierarchyNodeOptionsPopoverItems onItemSelected={nodeOptionsPopoverHooks.setResult} />
      </DefaultPopover>
    </>
  )
}
